import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

const SINGLE_CLICK_DELAY_MS = 220;
const HOVER_HIDE_DELAY_MS   = 150;
const LABEL_HIDE_DELAY_MS   = 100;

const usePlotlyEvents = ({
  isLabelMode,
  layout,
  annotations,
  labelsRawRef,
  sectionsRef,
  tRawRef,
  setHoveredLabel,
  timerEtiquette,
  handlePhaseClickInternal,
  onSignalClick,
  onLabelHover,
  onLabelUnhover,
  onPhaseClick,
  signalCount,
}) => {
  const [signalHover, setSignalHover] = useState(null);
  const plotElRef = useRef(null);
  const pendingSignalClickTimer = useRef(null);
  const signalHoverTimerRef = useRef(null);
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  const clearPendingSignalClick = useCallback(() => {
    if (pendingSignalClickTimer.current) {
      clearTimeout(pendingSignalClickTimer.current);
      pendingSignalClickTimer.current = null;
    }
  }, []);

  const cancelSignalHoverClear = useCallback(() => {
    clearTimeout(signalHoverTimerRef.current);
  }, []);

  const clearSignalHoverDelayed = useCallback(() => {
    signalHoverTimerRef.current = setTimeout(() => setSignalHover(null), HOVER_HIDE_DELAY_MS);
  }, []);

  useEffect(
    () => () => {
      clearPendingSignalClick();
      clearTimeout(signalHoverTimerRef.current);
    },
    [clearPendingSignalClick]
  );

  // LabelHoverCard (React) already shows all label details — no Plotly annotation needed.
  const activeLayout = useMemo(() => layout, [layout]);

  // Direct lookup via itemIdx and annMetaIdx embedded in customdata — no timestamp scan needed.
  const handleLabelHoverInternal = useCallback(
    (customdata, x, y) => {
      clearTimeout(timerEtiquette.current);
      const all = labelsRawRef.current;
      if (!all.length) return;
      const itemIdx = customdata?.itemIdx ?? null;
      const found = itemIdx != null ? all[itemIdx] : null;
      if (!found) return;
      setHoveredLabel({ label: found, x, y });
    },
    [setHoveredLabel]
  );

  const handleLabelUnhoverInternal = useCallback(() => {
    timerEtiquette.current = setTimeout(() => {
      setHoveredLabel(null);
    }, LABEL_HIDE_DELAY_MS);
  }, [setHoveredLabel]);

  const getNearestPoint = useCallback(
    (e, signalOnly = false) => {
      const points = e?.points || [];
      if (!points.length) return null;

      const nSignal = signalCount || 1;
      const candidates = signalOnly
        ? points.filter((p) => p?.curveNumber < nSignal)
        : points;

      if (!candidates.length) return null;
      if (candidates.length === 1) return candidates[0];

      const mouseX = e?.event?.clientX;
      const mouseY = e?.event?.clientY;
      if (mouseX == null || mouseY == null) return candidates[0];

      let best = candidates[0];
      let bestDist = Number.POSITIVE_INFINITY;

      candidates.forEach((p) => {
        const xa = p?.xaxis;
        const ya = p?.yaxis;
        if (
          !xa || !ya ||
          typeof xa.d2p !== 'function' ||
          typeof ya.d2p !== 'function' ||
          xa._offset == null || ya._offset == null
        ) return;
        try {
          const px = xa._offset + xa.d2p(p.x);
          const py = ya._offset + ya.d2p(p.y);
          const d = Math.hypot(px - mouseX, py - mouseY);
          if (d < bestDist) { bestDist = d; best = p; }
        } catch (_) {}
      });

      return best;
    },
    [signalCount]
  );

  const handleHover = useCallback(
    (e) => {
      const pt = getNearestPoint(e);
      if (!pt) return;

      const nSignal = signalCount || 1;

      if (pt.curveNumber < nSignal) {
        const allPoints = e?.points || [];
        const mouseY = e?.event?.clientY;
        const labelCandidates = allPoints.filter(
          (p) =>
            p.curveNumber >= nSignal &&
            p.customdata?.type === 'label'
        );
        const labelPt =
          mouseY != null && labelCandidates.length > 1
            ? labelCandidates.reduce((best, p) => {
                const ya = p?.yaxis;
                const ya2 = best?.yaxis;
                if (!ya?.d2p || !ya2?.d2p) return best;
                try {
                  const dY = Math.abs(ya._offset + ya.d2p(p.y) - mouseY);
                  const dB = Math.abs(ya2._offset + ya2.d2p(best.y) - mouseY);
                  return dY < dB ? p : best;
                } catch { return best; }
              })
            : labelCandidates[0] ?? null;
        if (labelPt) {
          if (!plotElRef.current)
            plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
          if (plotElRef.current) plotElRef.current.style.cursor = 'pointer';
          clearTimeout(signalHoverTimerRef.current);
          setSignalHover(null);
          if (isLabelMode) {
            handleLabelHoverInternal(labelPt.customdata, e.event?.clientX, e.event?.clientY);
          } else if (onLabelHover) {
            onLabelHover(labelPt.customdata?.t ?? labelPt.customdata, e.event?.clientX, e.event?.clientY);
          }
          return;
        }

        if (isLabelMode) {
          if (!plotElRef.current)
            plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
          // En mode pan/select/lasso, ne pas afficher le crosshair ni le popup signal
          if ((plotElRef.current?._fullLayout?.dragmode ?? 'zoom') !== 'zoom') return;
          clearTimeout(signalHoverTimerRef.current);
          if (plotElRef.current) plotElRef.current.style.cursor = 'crosshair';
          const ts = pt.pointNumber != null ? tRawRef.current?.[pt.pointNumber] : null;
          const tsDate =
            ts != null
              ? new Date(ts * 1000).toLocaleString('fr-FR', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })
              : null;
          setSignalHover({
            x: e.event?.clientX,
            y: e.event?.clientY,
            ts: ts != null ? Number(ts) : null,
            yVal: pt.y,
            curveNumber: pt.curveNumber,
            formula: pt.data?.meta?.formula || null,
            signalName: pt.data?.name || null,
            tsDate,
          });
        }
        return;
      }

      if (pt.customdata !== undefined) {
        if (pt.customdata?.type === 'phase') return;
        if (!plotElRef.current)
          plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
        if (plotElRef.current) plotElRef.current.style.cursor = 'pointer';
        if (isLabelMode) {
          handleLabelHoverInternal(pt.customdata, e.event?.clientX, e.event?.clientY);
        } else if (onLabelHover) {
          onLabelHover(pt.customdata?.t ?? pt.customdata, e.event?.clientX, e.event?.clientY);
        }
        return;
      }

    },
    [getNearestPoint, isLabelMode, handleLabelHoverInternal, onLabelHover, signalCount]
  );

  const handleUnhover = useCallback(() => {
    if (plotElRef.current) plotElRef.current.style.cursor = '';
    signalHoverTimerRef.current = setTimeout(() => setSignalHover(null), HOVER_HIDE_DELAY_MS);
    if (isLabelMode) {
      handleLabelUnhoverInternal();
    } else {
      if (onLabelUnhover) onLabelUnhover();
    }
  }, [isLabelMode, handleLabelUnhoverInternal, onLabelUnhover]);

  const handleClickAnnotation = useCallback(
    (e) => {
      if (e.index === undefined) return;
      const sec = sectionsRef.current[e.index];
      if (!sec) return;
      const phaseData = {
        type: 'phase',
        tStart: sec.tStart, tEnd: sec.tEnd,
        label: sec.label, color: sec.color,
      };
      if (isLabelMode) {
        handlePhaseClickInternal(phaseData);
      } else if (onPhaseClick) {
        onPhaseClick(phaseData, e.event?.clientX, e.event?.clientY);
      }
    },
    [isLabelMode, handlePhaseClickInternal, onPhaseClick]
  );

  const handleClick = useCallback(
    (e) => {
      const clickDetail = Number(e?.event?.detail || 1);
      if (clickDetail > 1) { clearPendingSignalClick(); return; }

      // Respecte le dragmode Plotly : pan/select/lasso → ne pas intercepter le clic
      if (!plotElRef.current && e?.event?.target)
        plotElRef.current = e.event.target.closest?.('.js-plotly-plot');
      const dragMode = plotElRef.current?._fullLayout?.dragmode ?? 'zoom';
      if (dragMode !== 'zoom') { clearPendingSignalClick(); return; }

      const nSignal = signalCount || 1;
      const signalPt = getNearestPoint(e, true);
      if (signalPt && signalPt.curveNumber < nSignal) {
        const ts = tRawRef.current?.[signalPt.pointNumber];
        clearPendingSignalClick();
        pendingSignalClickTimer.current = setTimeout(() => {
          onSignalClick?.({
            ts: Number(ts ?? signalPt.x),
            yVal: signalPt.y,
            curveIndex: signalPt.curveNumber,
            formula: signalPt.data?.meta?.formula || null,
            pointNumber: signalPt.pointNumber,
          });
          pendingSignalClickTimer.current = null;
        }, SINGLE_CLICK_DELAY_MS);
        return;
      }

      const pt = getNearestPoint(e);
      if (!pt) return;
      if (pt.customdata?.type !== 'phase') return;
      if (isLabelMode) {
        handlePhaseClickInternal(pt.customdata);
      } else if (onPhaseClick) {
        onPhaseClick(pt.customdata, e.event?.clientX, e.event?.clientY);
      }
    },
    [
      clearPendingSignalClick, getNearestPoint, isLabelMode,
      handlePhaseClickInternal, onSignalClick, onPhaseClick, signalCount,
    ]
  );

  const handleDoubleClick = useCallback(() => {
    clearPendingSignalClick();
  }, [clearPendingSignalClick]);

  return {
    activeLayout,
    handleHover,
    handleUnhover,
    handleClick,
    handleDoubleClick,
    handleClickAnnotation,
    signalHover,
    setSignalHover,
    cancelSignalHoverClear,
    clearSignalHoverDelayed,
  };
};

export default usePlotlyEvents;
